import React from "react";
import {
  Box,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";

import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import WarningIcon from "@mui/icons-material/Warning";

const SingleFieldSelector = ({
  showLabel = true,
  name = "Select Field",
  height = 46,
  fields = [],
  selectedValue,
  onSelectionChange,
  placeholder = "Choose a field...",
  width = { xs: "200px", sm: "50%", md: "33%" },
  labelPosition = "above",
  disabled = false,
  margin = 1,
  gapAfterLabel = 2,
  showIcon = false, // New parameter to enable icon functionality
  iconTooltip = "Additional info", // Tooltip text for the icon
}) => {
  const handleChange = (event, newValue) => {
    if (newValue !== null) {
      onSelectionChange?.(newValue);
    }
  };

  const containerHeight = height;
  const inputFontSize = Math.max(13, height * 0.32); // Dynamic font size

  const styles = {
    container: (theme) => ({
      display: "flex",
      flexDirection: labelPosition === "left" ? "row" : "column",
      alignItems: labelPosition === "left" ? "center" : "stretch",
      gap:
        labelPosition === "left"
          ? theme.spacing(gapAfterLabel)
          : theme.spacing(1),
      width: labelPosition === "left" ? width : width,
      m: margin,
      maxWidth: width,
      overflow: "hidden",
      maxHeight: containerHeight, // Use calculated height
      minHeight: containerHeight, // Use calculated height
      // paddingRight: theme.spacing(2),
    }),
    label: (theme) => ({
      flexShrink: 0,
      color: theme.palette.text.primary,
      marginBottom: 0,
      fontSize: "0.85rem",
      lineHeight: 1.2,
    }),
    autocomplete: (theme) => ({
      width: "100%",
      flexGrow: 1,
      overflow: "visible",
      "& .MuiAutocomplete-inputRoot": {
        flexWrap: "nowrap", // Prevent chip wrapping
        overflowX: "auto", // Enable horizontal scrolling
        padding: theme.spacing(0.55, 1), // Reduced vertical padding
        height: height, // Set explicit height
        minHeight: "unset", // Remove default min-height
        backgroundColor: theme.palette.background.paper,
        "&:hover .MuiOutlinedInput-notchedOutline": disabled
          ? {} // No hover effects when disabled
          : {
              borderColor: "#0b0b0b",
            },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
          borderWidth: 2,
        },
      },
      "& .MuiAutocomplete-tagContainer": {
        display: "flex",
        flexWrap: "nowrap", // Keep chips in single row
        gap: theme.spacing(0.5),
        padding: "2px 0",
        overflowX: "auto", // Enable scrolling for chips
        scrollbarWidth: "thin", // For Firefox
        "&::-webkit-scrollbar": {
          height: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[400],
          borderRadius: "2px",
        },
      },
      "& .MuiAutocomplete-paper": {
        maxHeight: "20px",
        overflowY: "auto",
        marginTop: "4px",
      },
    }),
  };

  return (
    <Box sx={styles.container}>
      {/* Label handling */}
      {showLabel && name && labelPosition === "above" && (
        <InputLabel component="span" sx={styles.label}>
          {name}
        </InputLabel>
      )}

      {showLabel && name && labelPosition === "left" && (
        <Typography component="span" sx={styles.label}>
          {name}
        </Typography>
      )}

      {/* Single Selection Autocomplete */}
      <FormControl
        fullWidth
        sx={{ flexGrow: 1, maxHeight: containerHeight, overflow: "hidden" }}
        disabled={disabled}
      >
        <Autocomplete
          // disableClearable={true}
          options={fields}
          value={selectedValue}
          onChange={handleChange}
          disabled={disabled}
          getOptionLabel={(option) => option?.label || String(option)}
          isOptionEqualToValue={(option, value) =>
            option?.id === value?.id || option?.label === value?.label
          }
          renderOption={(props, option, { selected }) => {
            const { key, ...restProps } = props; // Extract key from props
            return (
              <li key={key} {...restProps}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  {selected ? (
                    <RadioButtonCheckedIcon fontSize="small" />
                  ) : (
                    <RadioButtonUncheckedIcon fontSize="small" />
                  )}
                  {showIcon ? (
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {option.label}
                      </Typography>
                      {option.showStageIcon && (
                        <Tooltip
                          title={
                            typeof iconTooltip === "function"
                              ? iconTooltip(option)
                              : iconTooltip
                          }
                          arrow
                        >
                          <IconButton
                            size="small"
                            sx={{
                              p: 0.5,
                              color:
                                option.stage === "FAILED"
                                  ? "error.main"
                                  : "warning.main",
                            }}
                          >
                            <WarningIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ) : (
                    option.label
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={!selectedValue ? placeholder : ""}
              sx={{
                "& .MuiInputBase-root": {
                  minHeight: height - 4, // Account for border
                  height: height,
                  py: 0.5,
                  whiteSpace: "nowrap", // Prevent text wrapping
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiInputBase-input": {
                  whiteSpace: "nowrap", // Apply to actual input element
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: `${inputFontSize}px`, // Dynamic font
                  padding: "6px 4px !important", // Tighter padding
                },
              }}
            />
          )}
          sx={styles.autocomplete}
        />
      </FormControl>
    </Box>
  );
};

export default SingleFieldSelector;
